
import { useState } from 'react';

export default function Module5() {
  const [activeSection, setActiveSection] = useState<number | null>(null);
  const [selectedCase, setSelectedCase] = useState<number | null>(null);
  const [showAnswer, setShowAnswer] = useState<{ [key: number]: boolean }>({});

  const sections = [
    {
      id: 1,
      title: "案例分析（分组讨论后讲解）",
      subtitle: "真实场景实战演练",
      icon: "ri-file-list-3-line",
      content: {
        description: "通过三个典型案例，将理论知识运用到实际业务场景中，提升学员的实战能力",
        cases: [
          {
            id: 1,
            type: "一般贸易案例",
            color: "from-blue-500 to-blue-600",
            scenario: {
              title: "深圳蓝牙耳机工厂出口案例",
              background: "我司是深圳一家蓝牙耳机工厂，年出口额2000万美元，所有原材料采购均有增值税发票。一个美国老客户下了一个50万美元的订单。",
              question: "请问应选择哪种贸易方式？为什么？核心流程是什么？",
              companyProfile: {
                industry: "电子制造业",
                location: "深圳",
                experience: "5年出口经验",
                annualRevenue: "2000万美元",
                clientBase: "欧美市场为主"
              },
              orderDetails: {
                product: "蓝牙耳机",
                quantity: "50,000台",
                unitPrice: "10美元",
                totalValue: "50万美元",
                customer: "美国老客户",
                paymentTerms: "30% T/T预付，70% 见提单副本"
              }
            },
            analysis: {
              keyFactors: [
                {
                  factor: "企业性质",
                  description: "生产型工厂，有完整生产能力",
                  impact: "适合一般贸易方式"
                },
                {
                  factor: "发票获取能力",
                  description: "所有原材料采购均有增值税发票",
                  impact: "可享受完整出口退税政策"
                },
                {
                  factor: "订单规模",
                  description: "单票50万美元，属于大额订单",
                  impact: "一般贸易成本摊薄效应明显"
                },
                {
                  factor: "客户关系",
                  description: "美国老客户，合作基础良好",
                  impact: "风险可控，适合规范操作"
                }
              ],
              recommendation: {
                method: "一般贸易（0110）",
                reasons: [
                  "工厂有完整生产能力和进项发票",
                  "50万美元大额订单，退税收益可观",
                  "年出口额2000万，具备规范操作能力",
                  "与老客户合作，风险相对较低"
                ],
                expectedBenefits: [
                  "假设退税率13%，可获得约65万元退税",
                  "提升企业品牌形象和客户信任度",
                  "为后续更大规模合作奠定基础",
                  "符合企业长期发展战略"
                ]
              }
            },
            solution: {
              coreProcess: [
                {
                  step: "接单确认",
                  description: "与客户确认PI，签订正式出口合同",
                  timeframe: "1-2天",
                  keyPoints: ["价格条款确认", "交期安排", "质量标准"],
                  documents: ["出口合同", "形式发票"]
                },
                {
                  step: "采购生产",
                  description: "按订单要求采购原料并组织生产",
                  timeframe: "20-30天",
                  keyPoints: ["确保进项发票齐全", "质量控制", "进度跟踪"],
                  documents: ["采购合同", "增值税专用发票", "质检报告"]
                },
                {
                  step: "报关出口",
                  description: "制作单证，向海关申报出口",
                  timeframe: "2-3天",
                  keyPoints: ["单证一致性", "商品归类准确", "价格申报合理"],
                  documents: ["报关单", "发票", "装箱单", "合同"]
                },
                {
                  step: "货物装运",
                  description: "安排海运装箱并取得提单",
                  timeframe: "1-2天",
                  keyPoints: ["装箱监督", "提单确认", "运输保险"],
                  documents: ["提单", "装箱单", "保险单"]
                },
                {
                  step: "收汇核销",
                  description: "收取客户货款并办理外汇核销",
                  timeframe: "30-60天",
                  keyPoints: ["汇率确认", "到账核实", "核销及时"],
                  documents: ["银行水单", "收汇核销单"]
                },
                {
                  step: "办理退税",
                  description: "向税务部门申请出口退税",
                  timeframe: "90-180天",
                  keyPoints: ["单证齐全", "及时申报", "跟进进度"],
                  documents: ["退税申报表", "出口发票", "报关单", "收汇凭证"]
                }
              ],
              riskControl: [
                "建立完善的质量管理体系",
                "与客户保持密切沟通",
                "购买出口信用保险",
                "合理安排资金计划应对退税周期"
              ],
              successMetrics: [
                "按时交货率 ≥ 98%",
                "客户满意度 ≥ 95%",
                "退税申报成功率 100%",
                "资金回笼周期 ≤ 90天"
              ]
            }
          },
          {
            id: 2,
            type: "市场采购贸易案例",
            color: "from-purple-500 to-purple-600",
            scenario: {
              title: "亚马逊跨境电商卖家出口案例",
              background: "我是一个亚马逊卖家，主要在1688上采购家居用品、文具等上百种商品，发往美国海外仓。单个SKU价值低，且无法从多个小供应商处取得发票。",
              question: "总货值8万美元。如何在深圳出口？",
              businessProfile: {
                platform: "亚马逊美国站",
                experience: "3年跨境电商经验",
                sourceChannel: "1688批发平台",
                productRange: "家居用品、文具、小商品",
                skuCount: "100+种商品"
              },
              challenges: [
                "商品种类繁多，单价较低",
                "供应商分散，无法提供发票",
                "需要快速周转，减少资金占用",
                "海外仓模式要求灵活发货"
              ]
            },
            analysis: {
              keyFactors: [
                {
                  factor: "商品特性",
                  description: "多品种、小批量、低单价",
                  impact: "典型的1039适用场景"
                },
                {
                  factor: "发票获取难度",
                  description: "1688小供应商无法提供增值税发票",
                  impact: "一般贸易方式不适用"
                },
                {
                  factor: "货值规模",
                  description: "总货值8万美元，单票不超限",
                  impact: "符合1039单票15万美元限制"
                },
                {
                  factor: "业务模式",
                  description: "跨境电商B2C模式",
                  impact: "需要灵活的收汇方式"
                }
              ],
              recommendation: {
                method: "市场采购贸易（1039）",
                reasons: [
                  "解决无票货物合法出口问题",
                  "免征增值税，无需垫付税款",
                  "支持灵活收汇，适合跨境电商",
                  "简化申报，提高通关效率"
                ],
                expectedBenefits: [
                  "节省5-8%的税务成本",
                  "缩短50%的通关时间",
                  "提高资金周转效率",
                  "规避税务合规风险"
                ]
              }
            },
            solution: {
              operationSteps: [
                {
                  step: "选择代理服务商",
                  description: "委托广东天健商业广场的外综服公司",
                  timeframe: "1天",
                  keyPoints: ["代理资质审核", "服务费用确认", "操作流程对接"],
                  considerations: ["选择有经验的代理", "确认服务范围", "明确责任划分"]
                },
                {
                  step: "商品采购集货",
                  description: "从1688采购并运至代理仓库",
                  timeframe: "5-10天",
                  keyPoints: ["商品质量控制", "数量核对", "包装标准"],
                  considerations: ["避免侵权商品", "确保商品合规", "做好商品分类"]
                },
                {
                  step: "信息平台录入",
                  description: "在市场采购贸易联网信息平台录入详细信息",
                  timeframe: "1-2天",
                  keyPoints: ["商品信息准确", "交易信息完整", "供货商备案"],
                  considerations: ["确保信息真实性", "避免虚假申报", "配合监管要求"]
                },
                {
                  step: "组货装箱",
                  description: "按照海运要求进行组货和装箱",
                  timeframe: "2-3天",
                  keyPoints: ["合理搭配装载", "防护包装", "标识清晰"],
                  considerations: ["优化装箱方案", "确保货物安全", "便于客户分拣"]
                },
                {
                  step: "报关出口",
                  description: "使用1039贸易方式代码向海关申报",
                  timeframe: "1-2天",
                  keyPoints: ["按章目归并申报", "配合海关查验", "单证一致"],
                  considerations: ["简化申报内容", "确保合规操作", "及时响应查验"]
                },
                {
                  step: "灵活收汇",
                  description: "通过代理公司灵活收取美元货款",
                  timeframe: "10-30天",
                  keyPoints: ["汇率确认", "手续费结算", "资金安全"],
                  considerations: ["选择合适收汇时机", "控制汇率风险", "确保资金安全"]
                }
              ],
              platformAdvantages: [
                "支持境外个人账户收汇",
                "免征增值税政策支持",
                "简化通关申报程序",
                "政府监管透明规范"
              ],
              costAnalysis: {
                traditionalCosts: [
                  "增值税垫付：约10,000美元",
                  "财务处理费：2,000元/月",
                  "退税不确定风险"
                ],
                newModelCosts: [
                  "代理服务费：3-5‰",
                  "平台使用费：免费",
                  "简化操作成本"
                ],
                savings: "总成本节省约6-8万元人民币"
              }
            }
          },
          {
            id: 3,
            type: "混合模式案例",
            color: "from-orange-500 to-orange-600",
            scenario: {
              title: "混合贸易方式出口案例",
              background: "一个50万美元的订单，其中40万美元产品有发票，10万美元产品是市场采购的无票配件。",
              question: "如何操作？",
              orderBreakdown: {
                totalValue: "50万美元",
                invoicedPart: {
                  value: "40万美元", 
                  products: "主要产品（有进项发票）",
                  percentage: "80%"
                },
                noInvoicePart: {
                  value: "10万美元",
                  products: "配件（无票市场采购）", 
                  percentage: "20%"
                }
              },
              challenges: [
                "两种不同性质的货物混合",
                "需要分别享受不同政策优惠",
                "确保合规操作避免风险",
                "优化整体税务成本"
              ]
            },
            analysis: {
              keyFactors: [
                {
                  factor: "货物性质差异",
                  description: "有票和无票货物混合",
                  impact: "需要分别处理，不能混报"
                },
                {
                  factor: "政策适用性",
                  description: "两部分货物适用不同贸易方式",
                  impact: "需要分票操作，各享其利"
                },
                {
                  factor: "整体效益",
                  description: "40万可享受退税，10万免征增值税",
                  impact: "混合模式效益最大化"
                },
                {
                  factor: "合规要求",
                  description: "严禁混报，必须如实申报",
                  impact: "操作规范性要求极高"
                }
              ],
              recommendation: {
                method: "分票报关策略",
                reasons: [
                  "40万美元部分按一般贸易申报享受退税",
                  "10万美元部分按1039申报免征增值税",
                  "完全合规避免政策风险",
                  "整体税务成本最优化"
                ],
                expectedBenefits: [
                  "预计退税约34万元人民币（假设13%退税率）",
                  "节省增值税约8.5万元人民币",
                  "总体节省成本约40万元人民币",
                  "完全合规无政策风险"
                ]
              },
              prohibitions: [
                "严禁将有票货物按1039申报",
                "严禁将无票货物按一般贸易申报", 
                "严禁虚假申报货物性质",
                "严禁人为拆分规避监管"
              ]
            },
            solution: {
              operationStrategy: {
                title: "分票报关策略",
                description: "将订单按货物性质分成两票分别申报，确保合规操作",
                benefits: [
                  "40万美元部分享受出口退税",
                  "10万美元部分免征增值税",
                  "整体税务成本最优化",
                  "完全合规无风险"
                ]
              },
              detailedSteps: [
                {
                  category: "前期准备",
                  steps: [
                    {
                      action: "货物分类整理",
                      description: "将有票和无票货物严格分开管理",
                      requirements: ["建立货物清单", "分别核算成本", "准备不同单证"]
                    },
                    {
                      action: "选择操作主体",
                      description: "有票部分用一般贸易企业，无票部分委托1039代理",
                      requirements: ["确认企业资质", "签订代理协议", "明确责任分工"]
                    }
                  ]
                },
                {
                  category: "分票操作",
                  steps: [
                    {
                      action: "第一票：一般贸易申报",
                      description: "40万美元有票货物按一般贸易方式申报",
                      requirements: [
                        "贸易方式代码：0110",
                        "提供完整进项发票链",
                        "按标准流程制作单证",
                        "享受出口退税政策"
                      ],
                      expectedBenefit: "预计退税约34万元人民币（假设13%退税率）"
                    },
                    {
                      action: "第二票：市场采购贸易申报", 
                      description: "10万美元无票配件按1039方式申报",
                      requirements: [
                        "贸易方式代码：1039",
                        "在联网信息平台录入",
                        "委托试点市场代理操作",
                        "免征增值税"
                      ],
                      expectedBenefit: "节省增值税约8.5万元人民币"
                    }
                  ]
                }
              ],
              riskManagement: [
                {
                  risk: "混报风险",
                  prevention: "严格按货物实际性质分别申报，建立完善的货物管理制度",
                  consequence: "避免海关处罚和信用记录影响"
                },
                {
                  risk: "成本控制风险",
                  prevention: "精确核算两种方式的成本差异，优化整体方案",
                  consequence: "确保混合模式的经济效益"
                },
                {
                  risk: "操作复杂风险",
                  prevention: "建立标准操作流程，加强人员培训",
                  consequence: "提高操作效率和准确性"
                }
              ],
              successFactors: [
                "货物分类准确，单证制作规范",
                "两种贸易方式操作都符合规定",
                "与海关、税务部门沟通顺畅",
                "整体税务成本得到有效控制"
              ]
            }
          }
        ]
      }
    },
    {
      id: 2,
      title: "互动答疑环节",
      subtitle: "解决实际工作难题",
      icon: "ri-question-answer-line",
      content: {
        description: "针对学员在实际工作中遇到的问题进行深入解答，巩固学习效果",
        commonQuestions: [
          {
            category: "政策理解",
            questions: [
              {
                q: "一般贸易和市场采购贸易可以同时使用吗？",
                a: "完全可以。企业可以根据不同货物的特点选择不同的贸易方式，关键是要如实申报，不能混报。有发票的货物走一般贸易享受退税，无发票的货物走1039免征增值税。"
              },
              {
                q: "1039贸易方式对企业规模有要求吗？",
                a: "没有企业规模要求，但必须在经认定的试点市场内备案。小微企业、个体工商户都可以使用，这正是1039政策的初衷——服务中小微企业。"
              },
              {
                q: "选择贸易方式时最重要的考虑因素是什么？",
                a: "最重要的是发票获取能力。能取得增值税专用发票的优选一般贸易，无法取得发票的选择1039。其次考虑货值大小、企业规模、合规能力等因素。"
              }
            ]
          },
          {
            category: "操作实务",
            questions: [
              {
                q: "如何判断我的产品是否适合1039方式？",
                a: "主要看三点：1）是否在试点市场商品目录内；2）单ticket不超过15万美元；3）是否难以取得进项发票。符合这三点的商品都适合1039方式。"
              },
              {
                q: "1039代理费用一般是多少？",
                a: "代理费用通常在货值的3-8‰之间，具体费用取决于服务内容、货物复杂程度、代理商资质等。建议选择有经验、信誉好的代理商，不要只看价格。"
              },
              {
                q: "退税到账时间很长怎么办？",
                a: "正常退税周期是3-12个月。可以通过以下方式缓解：1）申请出口退税贷款；2）与银行办理退税托管业务；3）购买出口信用保险；4）合理安排资金计划。"
              }
            ]
          },
          {
            category: "风险防控",
            questions: [
              {
                q: "使用1039最容易出现什么问题？",
                a: "最常见的问题是：1）商品侵权风险；2）代理商选择不当；3）平台信息录入不准确；4）与实际贸易不符。预防方法是选择正规代理、如实申报、加强供应商管理。"
              },
              {
                q: "海关查验时需要注意什么？",
                a: "查验时要配合海关工作，如实说明货物情况。单证要齐全一致，货物要与申报相符。如果是1039货物，要能说明采购来源和贸易真实性。"
              },
              {
                q: "如何避免汇率风险？",
                a: "可以采用：1）远期结汇锁定汇率；2）选择合适的贸易术语；3）在合同中约定汇率波动条款；4）采用多币种结算分散风险；5）合理安排收汇时间。"
              }
            ]
          }
        ],
        discussionTopics: [
          {
            topic: "贸易方式选择的决策流程",
            points: [
              "如何系统性评估企业适合的贸易方式？",
              "不同发展阶段的企业如何调整贸易方式？",
              "如何平衡成本效益和风险控制？"
            ]
          },
          {
            topic: "新政策趋势与应对",
            points: [
              "RCEP等自贸协定对贸易方式选择的影响",
              "数字化转型对外贸流程的优化",
              "绿色贸易、可持续发展的新要求"
            ]
          },
          {
            topic: "实战经验分享",
            points: [
              "成功案例的关键成功因素",
              "失败案例的教训总结",
              "行业最佳实践的借鉴应用"
            ]
          }
        ]
      }
    },
    {
      id: 3,
      title: "知识巩固与总结",
      subtitle: "系统梳理核心要点",
      icon: "ri-bookmark-3-line",
      content: {
        description: "通过系统总结，帮助学员构建完整的知识体系，为实际应用打下坚实基础",
        keyTakeaways: [
          {
            category: "贸易方式选择原则",
            points: [
              "发票获取能力是首要考虑因素",
              "货值大小影响成本效益分析",
              "企业规模决定操作复杂度承受能力",
              "风险偏好影响具体方式选择"
            ]
          },
          {
            category: "操作要点提醒",
            points: [
              "一般贸易重点关注单证齐全和退税申报",
              "1039贸易重点关注平台录入和代理选择",
              "混合模式重点关注分类准确和合规操作",
              "所有方式都要确保贸易真实性"
            ]
          },
          {
            category: "风险防控建议",
            points: [
              "建立完善的内控制度",
              "选择专业的服务商",
              "保持与监管部门的良好沟通",
              "持续关注政策变化"
            ]
          }
        ],
        actionPlan: {
          immediate: [
            "评估现有业务适用的贸易方式",
            "梳理企业发票获取能力",
            "联系专业服务机构咨询",
            "制定贸易方式转换计划"
          ],
          shortTerm: [
            "完善企业内控制度",
            "培训相关业务人员",
            "建立供应商管理体系",
            "优化单证制作流程"
          ],
          longTerm: [
            "构建多元化贸易方式组合",
            "建立风险监控预警机制",
            "培养专业外贸团队",
            "建立可持续发展模式"
          ]
        }
      }
    }
  ];

  const toggleAnswer = (questionIndex: number) => {
    setShowAnswer(prev => ({
      ...prev,
      [questionIndex]: !prev[questionIndex]
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-red-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <i className="ri-graduation-cap-line text-white text-xl"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Pacifico, serif" }}>天健外贸培训学院</h1>
                <p className="text-sm text-gray-600">专业外贸知识培训平台</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => window.REACT_APP_NAVIGATE('/')}
                className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                返回首页
              </button>
              <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer">
                立即报名
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/794c90ad9648e2278f8fd3170e89e36c.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-red-900/90 to-pink-900/70"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-16">
          <div className="max-w-4xl">
            <div className="flex items-center space-x-3 mb-4">
              <span className="bg-red-500 text-white px-4 py-2 rounded-full text-sm font-medium">模块五</span>
              <span className="bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium">1小时</span>
            </div>
            <h1 className="text-5xl font-bold text-white mb-4 leading-tight">
              案例分析与互动答疑
            </h1>
            <p className="text-xl text-red-100 mb-6">
              巩固提升 · 通过真实场景应用所学知识，解决实际问题
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-white mb-3">培训目标：</h3>
              <p className="text-red-100 leading-relaxed">
                通过三个典型案例的深入分析和互动讨论，让学员能够将理论知识灵活运用到实际业务中，
                具备独立分析问题、制定解决方案的能力，成为真正的外贸实战专家。
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Course Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">详细内容</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            三大核心主题，通过实战案例巩固学习成果
          </p>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <div key={section.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div 
                className="bg-gradient-to-r from-red-600 to-red-700 p-6 cursor-pointer"
                onClick={() => setActiveSection(activeSection === section.id ? null : section.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <i className={`${section.icon} text-white text-xl`}></i>
                    </div>
                    <div>
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm font-medium">
                          主题{index + 1}
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-1">{section.title}</h3>
                      <p className="text-white/80 text-lg">{section.subtitle}</p>
                    </div>
                  </div>
                  <div className="text-white">
                    <i className={`${activeSection === section.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-2xl`}></i>
                  </div>
                </div>
              </div>
              
              {activeSection === section.id && (
                <div className="p-8">
                  {/* Section 1: 案例分析 */}
                  {section.id === 1 && (
                    <div>
                      <p className="text-lg text-gray-700 mb-8">{section.content.description}</p>
                      
                      {/* 案例选择导航 */}
                      <div className="mb-8">
                        <div className="flex flex-wrap justify-center gap-4">
                          {section.content.cases.map((caseItem, idx) => (
                            <button
                              key={idx}
                              onClick={() => setSelectedCase(selectedCase === caseItem.id ? null : caseItem.id)}
                              className={`px-6 py-3 rounded-xl font-semibold transition-colors ${
                                selectedCase === caseItem.id
                                  ? 'bg-red-500 text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              案例{caseItem.id}：{caseItem.type}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* 案例详细内容 */}
                      {section.content.cases.map((caseItem) => (
                        selectedCase === caseItem.id && (
                          <div key={caseItem.id} className="space-y-8">
                            {/* 案例背景 */}
                            <div className={`bg-gradient-to-r ${caseItem.color} rounded-2xl p-8 text-white`}>
                              <h4 className="text-2xl font-bold mb-4">{caseItem.scenario.title}</h4>
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div>
                                  <h5 className="font-bold mb-3">
                                    <i className="ri-building-line mr-2"></i>
                                    案例背景
                                  </h5>
                                  <p className="text-white/90 mb-4">{caseItem.scenario.background}</p>
                                  <div className="bg-white/10 rounded-lg p-4">
                                    <h6 className="font-semibold mb-2">核心问题</h6>
                                    <p className="text-white/90 text-sm">{caseItem.scenario.question}</p>
                                  </div>
                                </div>
                                <div>
                                  {caseItem.scenario.companyProfile && (
                                    <div className="bg-white/10 rounded-lg p-4">
                                      <h6 className="font-semibold mb-3">企业概况</h6>
                                      <div className="space-y-2 text-sm">
                                        <p><span className="font-medium">行业：</span>{caseItem.scenario.companyProfile.industry}</p>
                                        <p><span className="font-medium">地区：</span>{caseItem.scenario.companyProfile.location}</p>
                                        <p><span className="font-medium">经验：</span>{caseItem.scenario.companyProfile.experience}</p>
                                        <p><span className="font-medium">年出口额：</span>{caseItem.scenario.companyProfile.annualRevenue}</p>
                                      </div>
                                    </div>
                                  )}
                                  {caseItem.scenario.businessProfile && (
                                    <div className="bg-white/10 rounded-lg p-4">
                                      <h6 className="font-semibold mb-3">业务概况</h6>
                                      <div className="space-y-2 text-sm">
                                        <p><span className="font-medium">平台：</span>{caseItem.scenario.businessProfile.platform}</p>
                                        <p><span className="font-medium">经验：</span>{caseItem.scenario.businessProfile.experience}</p>
                                        <p><span className="font-medium">采购渠道：</span>{caseItem.scenario.businessProfile.sourceChannel}</p>
                                        <p><span className="font-medium">商品范围：</span>{caseItem.scenario.businessProfile.productRange}</p>
                                      </div>
                                    </div>
                                  )}
                                  {caseItem.scenario.orderBreakdown && (
                                    <div className="bg-white/10 rounded-lg p-4">
                                      <h6 className="font-semibold mb-3">订单构成</h6>
                                      <div className="space-y-2 text-sm">
                                        <p><span className="font-medium">订单总额：</span>{caseItem.scenario.orderBreakdown.totalValue}</p>
                                        <p><span className="font-medium">有票部分：</span>{caseItem.scenario.orderBreakdown.invoicedPart.value} ({caseItem.scenario.orderBreakdown.invoicedPart.percentage})</p>
                                        <p><span className="font-medium">无票部分：</span>{caseItem.scenario.orderBreakdown.noInvoicePart.value} ({caseItem.scenario.orderBreakdown.noInvoicePart.percentage})</p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* 分析过程 */}
                            <div className="bg-gray-50 rounded-2xl p-8">
                              <h4 className="text-2xl font-bold text-gray-900 mb-6">
                                <i className="ri-search-line mr-3"></i>
                                分析过程
                              </h4>
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {caseItem.analysis.keyFactors.map((factor, idx) => (
                                  <div key={idx} className="bg-white rounded-xl p-6 border border-gray-200">
                                    <h5 className="text-lg font-bold text-gray-900 mb-3">{factor.factor}</h5>
                                    <p className="text-gray-700 mb-3">{factor.description}</p>
                                    <div className="bg-blue-50 rounded-lg p-3">
                                      <p className="text-blue-800 text-sm font-medium">{factor.impact}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              
                              {caseItem.analysis.prohibitions && (
                                <div className="mt-6 bg-red-50 rounded-xl p-6 border border-red-200">
                                  <h5 className="text-lg font-bold text-red-900 mb-4">
                                    <i className="ri-error-warning-line mr-2"></i>
                                    重要禁止事项
                                  </h5>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {caseItem.analysis.prohibitions.map((prohibition, idx) => (
                                      <div key={idx} className="flex items-start space-x-2">
                                        <i className="ri-close-circle-line text-red-500 mt-1 flex-shrink-0"></i>
                                        <p className="text-red-800 text-sm">{prohibition}</p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* 推荐方案 */}
                            <div className="bg-green-50 rounded-2xl p-8 border border-green-200">
                              <h4 className="text-2xl font-bold text-green-900 mb-6">
                                <i className="ri-thumb-up-line mr-3"></i>
                                推荐方案
                              </h4>
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div>
                                  <h5 className="text-xl font-bold text-green-900 mb-4">选择：{caseItem.analysis.recommendation?.method || '待定'}</h5>
                                  <div className="space-y-3">
                                    <div>
                                      <h6 className="font-semibold text-green-900 mb-2">选择理由</h6>
                                      <ul className="space-y-2">
                                        {(caseItem.analysis.recommendation?.reasons || []).map((reason, idx) => (
                                          <li key={idx} className="flex items-start text-sm text-green-800">
                                            <i className="ri-check-line mr-2 mt-0.5 text-green-600 flex-shrink-0"></i>
                                            {reason}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <h6 className="font-semibold text-green-900 mb-3">预期收益</h6>
                                  <div className="space-y-2">
                                    {(caseItem.analysis.recommendation?.expectedBenefits || []).map((benefit, idx) => (
                                      <div key={idx} className="bg-white rounded-lg p-3">
                                        <p className="text-green-800 text-sm">{benefit}</p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* 解决方案详解 */}
                            <div className="bg-blue-50 rounded-2xl p-8">
                              <h4 className="text-2xl font-bold text-blue-900 mb-6">
                                <i className="ri-settings-line mr-3"></i>
                                解决方案详解
                              </h4>
                              
                              {caseItem.solution.coreProcess && (
                                <div className="mb-8">
                                  <h5 className="text-xl font-bold text-blue-900 mb-4">核心流程</h5>
                                  <div className="space-y-4">
                                    {caseItem.solution.coreProcess.map((step, idx) => (
                                      <div key={idx} className="bg-white rounded-xl p-6 border border-blue-200">
                                        <div className="flex items-start space-x-4">
                                          <div className="w-10 h-10 bg-blue-500 text-white rounded-lg flex items-center justify-center font-bold flex-shrink-0">
                                            {idx + 1}
                                          </div>
                                          <div className="flex-1">
                                            <div className="flex items-center justify-between mb-3">
                                              <h6 className="text-lg font-bold text-gray-900">{step.step}</h6>
                                              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                                                {step.timeframe}
                                              </span>
                                            </div>
                                            <p className="text-gray-700 mb-4">{step.description}</p>
                                            
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                              <div>
                                                <h6 className="font-semibold text-gray-900 mb-2">关键要点</h6>
                                                <ul className="space-y-1">
                                                  {step.keyPoints.map((point, pointIdx) => (
                                                    <li key={pointIdx} className="flex items-center text-sm text-gray-700">
                                                      <i className="ri-check-line mr-2 mt-0.5 text-green-500 flex-shrink-0"></i>
                                                      {point}
                                                    </li>
                                                  ))}
                                                </ul>
                                              </div>
                                              <div>
                                                <h6 className="font-semibold text-gray-900 mb-2">相关单据</h6>
                                                <ul className="space-y-1">
                                                  {step.documents.map((doc, docIdx) => (
                                                    <li key={docIdx} className="flex items-center text-sm text-gray-700">
                                                      <i className="ri-file-text-line mr-2 text-blue-500"></i>
                                                      {doc}
                                                    </li>
                                                  ))}
                                                </ul>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {caseItem.solution.operationSteps && (
                                <div className="mb-8">
                                  <h5 className="text-xl font-bold text-blue-900 mb-4">操作步骤</h5>
                                  <div className="space-y-4">
                                    {caseItem.solution.operationSteps.map((step, idx) => (
                                      <div key={idx} className="bg-white rounded-xl p-6 border border-blue-200">
                                        <div className="flex items-center justify-between mb-3">
                                          <h6 className="text-lg font-bold text-gray-900">{step.step}</h6>
                                          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                                            {step.timeframe}
                                          </span>
                                        </div>
                                        <p className="text-gray-700 mb-4">{step.description}</p>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                          <div>
                                            <h6 className="font-semibold text-gray-900 mb-2">关键要点</h6>
                                            <ul className="space-y-1">
                                              {step.keyPoints.map((point, pointIdx) => (
                                                <li key={pointIdx} className="flex items-start text-sm text-gray-700">
                                                  <i className="ri-check-line mr-2 mt-0.5 text-green-500 flex-shrink-0"></i>
                                                  {point}
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                          <div>
                                            <h6 className="font-semibold text-gray-900 mb-2">注意事项</h6>
                                            <ul className="space-y-1">
                                              {step.considerations.map((consideration, consIdx) => (
                                                <li key={consIdx} className="flex items-start text-sm text-gray-700">
                                                  <i className="ri-alert-line mr-2 mt-0.5 text-orange-500 flex-shrink-0"></i>
                                                  {consideration}
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {caseItem.solution.detailedSteps && (
                                <div className="mb-8">
                                  <h5 className="text-xl font-bold text-blue-900 mb-4">详细操作步骤</h5>
                                  <div className="space-y-6">
                                    {caseItem.solution.detailedSteps.map((category, idx) => (
                                      <div key={idx} className="bg-white rounded-xl p-6 border border-blue-200">
                                        <h6 className="text-lg font-bold text-gray-900 mb-4">{category.category}</h6>
                                        <div className="space-y-4">
                                          {category.steps.map((step, stepIdx) => (
                                            <div key={stepIdx} className="border-l-4 border-blue-300 pl-4">
                                              <h6 className="font-semibold text-gray-900 mb-2">{step.action}</h6>
                                              <p className="text-gray-700 mb-3">{step.description}</p>
                                              <div className="space-y-2">
                                                {step.requirements.map((req, reqIdx) => (
                                                  <div key={reqIdx} className="flex items-start text-sm text-gray-600">
                                                    <i className="ri-arrow-right-s-line mr-1 mt-0.5 flex-shrink-0"></i>
                                                    {req}
                                                  </div>
                                                ))}
                                              </div>
                                              {step.expectedBenefit && (
                                                <div className="mt-3 bg-green-50 rounded-lg p-3">
                                                  <p className="text-green-800 text-sm font-medium">{step.expectedBenefit}</p>
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  )}

                  {/* Section 2: 互动答疑 */}
                  {section.id === 2 && (
                    <div>
                      <p className="text-lg text-gray-700 mb-8">{section.content.description}</p>
                      
                      {/* 常见问题 */}
                      <div className="mb-12">
                        <h4 className="text-2xl font-bold text-gray-900 mb-8">
                          <i className="ri-question-line mr-3 text-red-600"></i>
                          常见问题解答
                        </h4>
                        <div className="space-y-6">
                          {section.content.commonQuestions.map((category, categoryIdx) => (
                            <div key={categoryIdx} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                              <div className="bg-red-50 px-6 py-4 border-b border-gray-200">
                                <h5 className="text-lg font-bold text-red-900">{category.category}</h5>
                              </div>
                              <div className="p-6">
                                <div className="space-y-4">
                                  {category.questions.map((qa, qaIdx) => {
                                    const questionIndex = categoryIdx * 100 + qaIdx;
                                    return (
                                      <div key={qaIdx} className="border border-gray-200 rounded-lg overflow-hidden">
                                        <button
                                          onClick={() => toggleAnswer(questionIndex)}
                                          className="w-full px-6 py-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
                                        >
                                          <div className="flex items-center justify-between">
                                            <h6 className="font-semibold text-gray-900">{qa.q}</h6>
                                            <i className={`${showAnswer[questionIndex] ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-gray-600`}></i>
                                          </div>
                                        </button>
                                        {showAnswer[questionIndex] && (
                                          <div className="px-6 py-4 bg-white border-t border-gray-200">
                                            <p className="text-gray-700 leading-relaxed">{qa.a}</p>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* 讨论话题 */}
                      <div>
                        <h4 className="text-2xl font-bold text-gray-900 mb-8">
                          <i className="ri-discuss-line mr-3 text-red-600"></i>
                          深度讨论话题
                        </h4>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                          {section.content.discussionTopics.map((topic, idx) => (
                            <div key={idx} className="bg-white rounded-2xl p-6 border border-gray-200">
                              <h5 className="text-lg font-bold text-gray-900 mb-4">{topic.topic}</h5>
                              <div className="space-y-3">
                                {topic.points.map((point, pointIdx) => (
                                  <div key={pointIdx} className="flex items-start space-x-3">
                                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                                    <p className="text-gray-700 text-sm leading-relaxed">{point}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Section 3: 知识巩固与总结 */}
                  {section.id === 3 && (
                    <div>
                      <p className="text-lg text-gray-700 mb-8">{section.content.description}</p>
                      
                      {/* 核心要点 */}
                      <div className="mb-12">
                        <h4 className="text-2xl font-bold text-gray-900 mb-8">
                          <i className="ri-key-2-line mr-3 text-red-600"></i>
                          核心要点回顾
                        </h4>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                          {section.content.keyTakeaways.map((takeaway, idx) => (
                            <div key={idx} className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-6 border border-red-200">
                              <h5 className="text-lg font-bold text-red-900 mb-4">{takeaway.category}</h5>
                              <div className="space-y-3">
                                {takeaway.points.map((point, pointIdx) => (
                                  <div key={pointIdx} className="flex items-start space-x-3">
                                    <i className="ri-check-double-line text-red-600 mt-1 flex-shrink-0"></i>
                                    <p className="text-red-800 text-sm leading-relaxed">{point}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* 行动计划 */}
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8">
                        <h4 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                          <i className="ri-roadmap-line mr-3 text-blue-600"></i>
                          实施行动计划
                        </h4>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                          <div className="bg-white rounded-xl p-6 border border-blue-200">
                            <h5 className="text-lg font-bold text-blue-900 mb-4">
                              <i className="ri-timer-flash-line mr-2"></i>
                              立即行动 (1周内)
                            </h5>
                            <div className="space-y-3">
                              {section.content.actionPlan.immediate.map((action, idx) => (
                                <div key={idx} className="flex items-start space-x-3">
                                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-blue-600 text-xs font-bold">{idx + 1}</span>
                                  </div>
                                  <p className="text-gray-700 text-sm">{action}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          <div className="bg-white rounded-xl p-6 border border-blue-200">
                            <h5 className="text-lg font-bold text-blue-900 mb-4">
                              <i className="ri-calendar-line mr-2"></i>
                              短期目标 (1-3个月)
                            </h5>
                            <div className="space-y-3">
                              {section.content.actionPlan.shortTerm.map((action, idx) => (
                                <div key={idx} className="flex items-start space-x-3">
                                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-green-600 text-xs font-bold">{idx + 1}</span>
                                  </div>
                                  <p className="text-gray-700 text-sm">{action}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          <div className="bg-white rounded-xl p-6 border border-blue-200">
                            <h5 className="text-lg font-bold text-blue-900 mb-4">
                              <i className="ri-flag-line mr-2"></i>
                              长期规划 (6-12个月)
                            </h5>
                            <div className="space-y-3">
                              {section.content.actionPlan.longTerm.map((action, idx) => (
                                <div key={idx} className="flex items-start space-x-3">
                                  <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-purple-600 text-xs font-bold">{idx + 1}</span>
                                  </div>
                                  <p className="text-gray-700 text-sm">{action}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-red-600 to-pink-600 py-16">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="text-4xl font-bold text-white mb-4">
            恭喜完成外贸培训课程！
          </h2>
          <p className="text-xl text-red-100 mb-8 leading-relaxed">
            从理论到实践，从基础到精通，您已经具备了成为外贸专家的核心技能
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="px-8 py-4 bg-white text-red-600 rounded-lg hover:bg-gray-50 transition-colors font-semibold whitespace-nowrap cursor-pointer">
              <i className="ri-medal-line mr-2"></i>
              获取结业证书
            </button>
            <button className="px-8 py-4 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors backdrop-blur-sm font-semibold whitespace-nowrap cursor-pointer">
              <i className="ri-customer-service-line mr-2"></i>
              专家咨询服务
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <i className="ri-graduation-cap-line text-white"></i>
                </div>
                <h3 className="text-xl font-bold" style={{ fontFamily: "Pacifico, serif" }}>天健外贸培训学院</h3>
              </div>
              <p className="text-gray-400 mb-4">专业的外贸知识培训平台，助您成为外贸专家</p>
              <div className="flex space-x-4">
                <i className="ri-wechat-line text-xl text-gray-400 hover:text-white cursor-pointer transition-colors"></i>
                <i className="ri-qq-line text-xl text-gray-400 hover:text-white cursor-pointer transition-colors"></i>
                <i className="ri-mail-line text-xl text-gray-400 hover:text-white cursor-pointer transition-colors"></i>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">课程内容</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">外贸基础入门</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">一般贸易解析</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">市场采购贸易</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">风险防控</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">学习支持</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">在线答疑</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">实战案例</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">学习资料</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">证书认证</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">快速链接</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">关于我们</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">联系方式</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">服务条款</a></li>
                <li><a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors cursor-pointer">Made with Readdy</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 天健外贸培训学院. 保留所有权利.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
